import { useTranslation } from '@msdk/common'
import {
  FieldDefinition,
  FieldType,
  FormFieldDefinition,
  FormNode,
  FormOpenMode,
  ReloadViewsOptions,
} from '@msdk/definitions'
import { styled, Tooltip } from '@msdk/ui'
import React, { ReactElement, useEffect, useRef } from 'react'

import { ChangeArgs, ExternalValidation } from '../../models/form'
import { getValidationTarget } from '../ValidationPanel/validation-panel-utils'
import { AnyOfField } from './AnyOfField'
import { AttachmentField } from './AttachmentField'
import { ConditionalFieldContainer } from './ConditionalFieldContainer'
import { CustomField } from './CustomField'
import { DateTimeField } from './DateTimeField'
import { DateTimeFieldUnknown } from './DateTimeFieldUnknown'
import { DurationField } from './DurationField'
import { ListFields } from './ListFields'
import { LookupField } from './LookupField'
import { MetadataSelector } from './MetadataSelector'
import { MultiselectorField } from './MultiselectorField'
import { NumericField } from './NumericField'
import { OptionsetField } from './OptionsetField'
import { StringField } from './StringField'
import { TimeField } from './TimeField'

type CommonProps<T> = {
  type: FieldType
  node: FormNode
  className?: string
  filterField?: boolean
  size?: number
  showLabel?: boolean
  showPlaceholder?: boolean
  useLabelAsPlaceholder?: boolean
  onReload?: (options?: ReloadViewsOptions) => void
  moveToTab?: (newValue: number) => void
} & T

type ConditionalFieldRendererReadonlyProps = {
  readonly: true
}

type ConditionalFieldRendererEditProps = {
  readonly?: false
  onChange: (args: ChangeArgs) => void
  onBlur?: (node: FormNode) => void
  onExternalValidation?: (owner: FormNode, target: FormNode, validation: ExternalValidation | null) => void
}

export type ConditionalFieldRendererBehaviourProps =
  | ConditionalFieldRendererReadonlyProps
  | ConditionalFieldRendererEditProps

type ConditionalFieldRendererProps = CommonProps<ConditionalFieldRendererBehaviourProps>

const emptyOnChange = () => {
  //
}

const StyledError = styled('div')`
  width: 100%;
  height: 40px;
  background-color: red;
  color: white;
  text-align: center;
  border-radius: 5px;
  padding: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`

function UnsupportedComponent(props: { node: FormNode; type: FieldType }) {
  const fieldDef = props.node.value.fieldDefinition as FieldDefinition
  const t = useTranslation()

  return (
    <Tooltip title={`FieldName: ${props.node.id}, type: ${props.type}`}>
      <StyledError>{`The field ${t(fieldDef.idml, fieldDef.description)} is not supported`}</StyledError>
    </Tooltip>
  )
}

export function ConditionalFieldRenderer({
  type,
  node,
  className,
  filterField,
  onReload,
  ...rest
}: ConditionalFieldRendererProps) {
  const formDef = node.value.formDefinition as FormFieldDefinition
  const showLabel = rest.showLabel ?? !formDef.hideDescription
  const hasCustomUI = Boolean(formDef.customUI)
  const containerRef = useRef<HTMLDivElement>(null)
  const interactions = useRef(new Set<symbol>())
  const pendingBlur = useRef(false)
  const blurTimer = useRef<ReturnType<typeof setTimeout>>()
  const checkBlur = useRef<() => void>()

  const beginInteraction = () => {
    const interaction = Symbol()
    interactions.current.add(interaction)

    return () => {
      if (!interactions.current.delete(interaction) || !containerRef.current) {
        return
      }

      // Popup closure can restore focus to the field after its close callback.
      clearTimeout(blurTimer.current)
      blurTimer.current = setTimeout(() => checkBlur.current?.(), 0)
    }
  }

  useEffect(() => {
    const active = interactions.current
    return () => {
      clearTimeout(blurTimer.current)
      active.clear()
    }
  }, [])

  const fieldArgs = {
    formOpenMode: rest.readonly ? FormOpenMode.Readonly : FormOpenMode.Edit,
    filterField,
    showLabel,
    showPlaceholder: rest.showPlaceholder,
    useLabelAsPlaceholder: rest.useLabelAsPlaceholder,
    onReload,
    onFieldBlur: rest.readonly ? undefined : rest.onBlur,
    beginInteraction: rest.readonly ? undefined : beginInteraction,
    setExternalValidation:
      rest.readonly || !rest.onExternalValidation
        ? undefined
        : (target: FormNode, validation: ExternalValidation | null) =>
            rest.onExternalValidation?.(node, target, validation),
    moveToTab: rest.moveToTab,
  }

  const ruleEngineDebug = window.__APP_CONFIG__.DEBUG_RULE_ENGINE === 'true'

  const onChange = rest.readonly ? emptyOnChange : rest.onChange
  const onBlur = rest.readonly ? undefined : rest.onBlur
  const { size } = rest

  checkBlur.current = () => {
    if (!pendingBlur.current || interactions.current.size > 0 || !containerRef.current) {
      return
    }

    pendingBlur.current = false
    if (!onBlur || containerRef.current.contains(document.activeElement)) {
      return
    }

    node.value.flushChange?.()
    onBlur(node)
  }

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!onBlur || event.currentTarget.contains(event.relatedTarget)) {
      return
    }

    if (event.target.closest('[data-validation-target]') !== event.currentTarget) {
      return
    }

    pendingBlur.current = true
    checkBlur.current?.()
  }

  const renderer: Record<FieldType, ReactElement> = {
    Attachment: <AttachmentField onChange={onChange} node={node} fieldArgs={fieldArgs} />,
    Bag: <UnsupportedComponent node={node} type={type} />,
    Custom: <CustomField onChange={onChange} node={node} fieldArgs={fieldArgs} />,
    DateTime: <DateTimeField onChange={onChange} node={node} fieldArgs={fieldArgs} />,
    DateTimeUnknown: <DateTimeFieldUnknown onChange={onChange} node={node} fieldArgs={fieldArgs} />,
    Duration: <DurationField onChange={onChange} node={node} fieldArgs={fieldArgs} />,
    Image: <AttachmentField onChange={onChange} node={node} fieldArgs={fieldArgs} />,
    List: <ListFields onChange={onChange} node={node} fieldArgs={fieldArgs} />,
    Lookup: <LookupField onChange={onChange} node={node} fieldArgs={fieldArgs} />,
    Numeric: <NumericField onChange={onChange} node={node} fieldArgs={fieldArgs} />,
    OptionSet: <OptionsetField onChange={onChange} node={node} fieldArgs={fieldArgs} />,
    String: <StringField onChange={onChange} node={node} fieldArgs={fieldArgs} />,
    MultiSelector: <MultiselectorField onChange={onChange} node={node} fieldArgs={fieldArgs} />,
    MetadataSelector: <MetadataSelector onChange={onChange} node={node} fieldArgs={fieldArgs} />,
    Time: <TimeField onChange={onChange} node={node} fieldArgs={fieldArgs} />,
    AnyOf: <AnyOfField onChange={onChange} node={node} fieldArgs={fieldArgs} />,
    CodeableConcept: <StringField onChange={onChange} node={node} fieldArgs={fieldArgs} />,
  }

  if (node.value.visibility === 'Invisible') {
    return
  }

  const classNames = ['ConditionalField-renderer', className].filter(Boolean).join(' ')

  return (
    <>
      {ruleEngineDebug && (
        <Tooltip title={node.id}>
          <ConditionalFieldContainer
            ref={containerRef}
            filterField={filterField || false}
            hasCustomUI={hasCustomUI}
            className={classNames}
            data-validation-node={node.id}
            data-validation-target={getValidationTarget(node)}
            onBlurCapture={handleBlur}
            size={size}
            tabIndex={hasCustomUI ? -1 : undefined}
          >
            {renderer[type]}
          </ConditionalFieldContainer>
        </Tooltip>
      )}
      {!ruleEngineDebug && (
        <ConditionalFieldContainer
          ref={containerRef}
          filterField={filterField || false}
          hasCustomUI={hasCustomUI}
          className={classNames}
          data-validation-node={node.id}
          data-validation-target={getValidationTarget(node)}
          onBlurCapture={handleBlur}
          size={size}
          tabIndex={hasCustomUI ? -1 : undefined}
        >
          {renderer[type]}
        </ConditionalFieldContainer>
      )}
    </>
  )
}
